import { ReactElement, useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../ui-components'
import { defaultTheme } from '../../../theme'
import { Delete } from '@mui/icons-material'
import {
  getInCartProductQty,
  ICartProductData,
  removeProductThunk,
  viewCartLink,
} from 'cart'
import { observer } from 'mobx-react-lite'
import { useUserStore } from '../../../store/user/UserStore'
import { Vehicle } from '../../../store/models/Vehicles'
import { ISeller } from '../../../services/MultiSellerServicesProvider'
import { Translate } from 'common'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { ProductLocationModel } from 'interfaces'
import { useSelector } from 'react-redux'
import { RootState, store } from 'store'
import { LocationType } from 'src/helpers/locationType'

interface Props {
  seller: ISeller
  vehicle: Vehicle
  location: ProductLocationModel
  cartProduct: ICartProductData
  qty: number
  disableButton: boolean
}

const AddToCartButton = observer<Props>((props: Props): ReactElement => {
  const { seller, vehicle, location, cartProduct, qty, disableButton } = props
  const [loading, setLoading] = useState(false)

  const { product, disableAddToCart, handleAddVehiclePartToCart } = cartProduct

  const userStore = useUserStore()
  const uiStore = useUiStateStore()

  const isPrimary = location.locType === LocationType.PRIMARY
  const orderIfNotAvailable =
    userStore?.preferences?.findit_orderIfNotAvail === 'true'

  const handleAdd = async () => {
    try {
      setLoading(true)
      await handleAddVehiclePartToCart(vehicle, location, qty)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = () => {
    if (!inCart) return
    if (seller) {
      store.dispatch(
        removeProductThunk({
          product,
          quantity: qty,
          locationId: location.locationId.toString(),
          vehicle,
          seller,
          showToastMessage: false,
        })
      )
    }

    uiStore.displaySuccessNotification(productRemovedFromCartMsg, viewCartLink)
  }

  const productRemovedFromCartMsg = Translate('partWasRemovedFromYourCart', [
    product.description || '',
  ])

  const inCartQty = useSelector((state: RootState) =>
    getInCartProductQty(
      state,
      vehicle,
      seller?.coverageKey ?? '',
      product,
      location.locationId
    )
  )

  const inCart = inCartQty > 0
  const inCartText = `${inCartQty} ${Translate('inCart')}`

  const availableQty =
    location.qtyAvailable >= inCartQty &&
    location.qtyAvailable >= qty &&
    location.qtyAvailable > 0
  const available = availableQty || (isPrimary && orderIfNotAvailable)
  const disabled = !available || disableAddToCart || disableButton

  return (
    <>
      {!inCart && (
        <ButtonWrapper>
          <AddButton
            loading={loading}
            onClick={handleAdd}
            text="addToCart"
            disabled={disabled}
          />
        </ButtonWrapper>
      )}

      {inCart && (
        <ButtonWrapper>
          <AddButton text={inCartText} variant="success" />
          <DeleteWrapper onClick={handleRemove}>
            <DeleteButton />
          </DeleteWrapper>
        </ButtonWrapper>
      )}
    </>
  )
})

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0 0;
  width: 168px;
  box-sizing: border-box;
  gap: 4px;
`

const AddButton = styled(Button)`
  flex: 1;
  min-width: 0;
  width: auto;
  margin: 0;
`

const DeleteWrapper = styled.div`
  padding: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${defaultTheme.colors.siteBackground};
  cursor: pointer;
  border-radius: 3px;
  flex-shrink: 0;
`

const DeleteButton = styled(Delete)`
  color: ${defaultTheme.colors.greyDark};
`

export default AddToCartButton
